const TOKEN_KEY = 'jwt_token';
const USERNAME_KEY = 'username';
const API_KEY_KEY = 'api_key';

const LOCAL_DEV_TOKEN = 'local-dev-token';
const LOCAL_DEV_USER = 'dev-user';

// Refresh the JWT this many ms before it actually expires, so in-flight
// requests never race the expiry boundary.
const REFRESH_SKEW_MS = 30_000;
// Never schedule a refresh sooner than this, to avoid a tight loop if the
// backend ever issues a very short-lived token.
const MIN_REFRESH_DELAY_MS = 5_000;

class AuthService {
  static refreshTimer = null;

  static isLocalDev() {
    return window.APP_CONFIG?.localDev === true;
  }

  // Exchange an EDL api_key for a fresh JWT via /auth/verify.
  static async verify(apiKey) {
    const verifyRes = await fetch(`${process.env.PUBLIC_URL}/auth/verify`, {
      method: 'GET',
      headers: { 'apikey': apiKey },
    });

    if (!verifyRes.ok) {
      throw new Error(`Verify failed: ${verifyRes.status}`);
    }

    const jwt = verifyRes.headers.get('X-JWT');
    if (!jwt) throw new Error('Verify did not return X-JWT header');

    localStorage.setItem(TOKEN_KEY, jwt);
    return jwt;
  }

  static async login(username, password) {
    if (this.isLocalDev()) {
      localStorage.setItem(TOKEN_KEY, LOCAL_DEV_TOKEN);
      localStorage.setItem(USERNAME_KEY, username || LOCAL_DEV_USER);
      return LOCAL_DEV_TOKEN;
    }

    const loginRes = await fetch(`${process.env.PUBLIC_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!loginRes.ok) {
      throw new Error(`EDL login failed: ${loginRes.status}`);
    }

    const { api_key } = await loginRes.json();
    if (!api_key) throw new Error('EDL did not return api_key');

    const jwt = await this.verify(api_key);

    localStorage.setItem(API_KEY_KEY, api_key);
    localStorage.setItem(USERNAME_KEY, username);
    this.scheduleRefresh();
    return jwt;
  }

  // Mint a new JWT from the stored api_key and reschedule the next refresh.
  // On failure (e.g. the api_key itself expired) the session is ended.
  static async refresh() {
    if (this.isLocalDev()) return LOCAL_DEV_TOKEN;

    const apiKey = localStorage.getItem(API_KEY_KEY);
    if (!apiKey) {
      this.handleSessionExpired();
      return null;
    }

    try {
      const jwt = await this.verify(apiKey);
      this.scheduleRefresh();
      return jwt;
    } catch (err) {
      this.handleSessionExpired();
      return null;
    }
  }

  // Decode the JWT `exp` (seconds since epoch) without verifying the signature.
  static getTokenExpiry(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  // Schedule a single proactive refresh shortly before the current JWT expires.
  static scheduleRefresh() {
    if (this.isLocalDev()) return;

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const expiresAt = this.getTokenExpiry(token);
    if (!expiresAt) return; // Can't tell when it expires; fall back to 401 handling.

    const delay = Math.max(expiresAt - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS);
    this.refreshTimer = setTimeout(() => this.refresh(), delay);
  }

  static handleSessionExpired() {
    this.logout();
    // Prefix the app base path so the redirect respects the router basename
    // (e.g. /ordering/login), not the origin root.
    window.location.href = `${process.env.PUBLIC_URL || ''}/login`;
  }

  static logout() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
    localStorage.removeItem(API_KEY_KEY);
  }

  static getToken() {
    if (this.isLocalDev()) return LOCAL_DEV_TOKEN;
    return localStorage.getItem(TOKEN_KEY);
  }

  static getUsername() {
    if (this.isLocalDev()) return localStorage.getItem(USERNAME_KEY) || LOCAL_DEV_USER;
    return localStorage.getItem(USERNAME_KEY);
  }

  static isAuthenticated() {
    if (this.isLocalDev()) return true;
    return !!localStorage.getItem(TOKEN_KEY);
  }
}

export default AuthService;
